import { useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { SidePanelProfile } from 'src/graphql/entities/side-panel-personalization/model/layout.types';
import { parseGroupProfile } from 'src/graphql/entities/side-panel-personalization/model/profiles';
import { GET_MY_USER_GROUP_SETTING_VALUES_QUERY } from 'src/graphql/entities/side-panel-personalization/side-panel-personalization.operations';
import { UserSettingId } from 'src/graphql/entities/user-setting-value/user-setting-value.types';

interface GetMyUserGroupSettingValuesQuery {
  getMyUserGroupSettingValues: {
    userGroupId: string;
    userSettingId: string;
    value: string | null;
  }[];
}

export function useMyGroupProfiles(groupProfileSettingId: UserSettingId): {
  groupProfiles: SidePanelProfile[];
  loading: boolean;
} {
  // fetched for every user — group profiles apply read-only even to users
  // without the personalize rule (see resolveEffectiveLayout); the op is in the
  // Bearer query set, so it does not 401
  const { data, loading } = useQuery<GetMyUserGroupSettingValuesQuery>(
    GET_MY_USER_GROUP_SETTING_VALUES_QUERY,
    { context: { suppressUnauthorizedToast: true } },
  );

  const groupProfiles = useMemo(
    () =>
      (data?.getMyUserGroupSettingValues ?? []).flatMap((row) => {
        if (row.userSettingId !== (groupProfileSettingId as string)) {
          return [];
        }

        const profile = parseGroupProfile(row.value, row.userGroupId);
        return profile ? [profile] : [];
      }),
    [data?.getMyUserGroupSettingValues, groupProfileSettingId],
  );

  return { groupProfiles, loading: loading && !data };
}
